import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DocumentPermissionTO } from "../../../api/models/document-permission-to";
import { OutlineFilterStatus } from "../../../components/cpm/workflowStatusCategory";
import { RootState } from "../../store/store";

export type SearchStatus = "idle" | "loading" | "success" | "error";
export type NextPageStatus = "idle" | "loading" | "error";

export interface OutlineSearchState {
  query: string;
  effectiveQuery: string | null;
  status: SearchStatus;
  results: DocumentPermissionTO[];
  page: number;
  hasMore: boolean;
  nextPageStatus: NextPageStatus;
  error: string | null;
  scopeRootId: string | null;
  selectedDocPartId: string | null;
  selectedScopeRootId: string | null;
  requestId: string | null;
  assignedToMe: boolean;
  statusFilter: OutlineFilterStatus | null;
}

interface SearchPageResult {
  content: DocumentPermissionTO[];
  hasMore: boolean;
}

const initialState: OutlineSearchState = {
  query: "",
  effectiveQuery: null,
  status: "idle",
  results: [],
  page: 0,
  hasMore: false,
  nextPageStatus: "idle",
  error: null,
  scopeRootId: null,
  selectedDocPartId: null,
  selectedScopeRootId: null,
  requestId: null,
  assignedToMe: false,
  statusFilter: null,
};

/**
 * IDS-7324: minimum number of (trimmed) characters a typed search term must reach before it
 * counts as active search criteria on its own. A filter (assignedToMe / statusFilter) is
 * independently sufficient regardless of this minimum - see hasActiveSearchCriteria.
 */
export const MIN_SEARCH_TERM_LENGTH = 3;

/**
 * Single source of truth for "is there any active search criteria (text and/or filter)".
 * Every gating site (selectors here, useOutlineSearch (FE-4), searchDocumentParts params (FE-7))
 * MUST call this instead of inlining a `query.trim().length >= 3` check.
 */
export const hasActiveSearchCriteria = (
  query: string,
  assignedToMe: boolean,
  statusFilter: OutlineFilterStatus | null
): boolean => query.trim().length >= MIN_SEARCH_TERM_LENGTH || assignedToMe || statusFilter !== null;

const outlineSearchSlice = createSlice({
  name: "outlineSearch",
  initialState,
  reducers: {
    queryChanged(state, action: PayloadAction<string>) {
      state.query = action.payload;
      // A filter can keep the search "active" even when the text drops below the 3-char threshold.
      if (!hasActiveSearchCriteria(action.payload, state.assignedToMe, state.statusFilter)) {
        state.status = "idle";
        state.results = [];
        state.effectiveQuery = null;
        state.page = 0;
        state.hasMore = false;
        state.error = null;
        state.nextPageStatus = "idle";
        // Clear requestId so any in-flight promise cannot commit stale results
        state.requestId = null;
      }
    },
    searchStarted(state, action: PayloadAction<{ reqId: string; q: string }>) {
      state.status = "loading";
      state.results = [];
      state.page = 0;
      state.hasMore = false;
      state.requestId = action.payload.reqId;
      state.effectiveQuery = action.payload.q;
      state.error = null;
      state.nextPageStatus = "idle";
    },
    searchSucceeded(state, action: PayloadAction<{ reqId: string; p: SearchPageResult }>) {
      if (action.payload.reqId !== state.requestId) return;
      state.status = "success";
      state.results = action.payload.p.content;
      state.hasMore = action.payload.p.hasMore;
      state.page = 0;
      state.error = null;
    },
    searchFailed(state, action: PayloadAction<{ reqId: string; err: string }>) {
      if (action.payload.reqId !== state.requestId) return;
      state.status = "error";
      state.error = action.payload.err;
    },
    nextPageStarted(state, action: PayloadAction<{ reqId: string }>) {
      if (action.payload.reqId !== state.requestId) return;
      state.nextPageStatus = "loading";
    },
    nextPageSucceeded(
      state,
      action: PayloadAction<{ reqId: string; p: SearchPageResult; page: number; effectiveQuery: string }>
    ) {
      if (action.payload.reqId !== state.requestId) return;
      if (action.payload.effectiveQuery !== state.effectiveQuery) return;
      state.results = [...state.results, ...action.payload.p.content];
      state.page = action.payload.page;
      state.hasMore = action.payload.p.hasMore;
      state.nextPageStatus = "idle";
    },
    nextPageFailed(state, action: PayloadAction<{ reqId: string; err: string }>) {
      if (action.payload.reqId !== state.requestId) return;
      state.nextPageStatus = "error";
      state.error = action.payload.err;
    },
    scopeRootResolved(state, action: PayloadAction<{ rootId: string | null }>) {
      const newRootId = action.payload.rootId;
      const changed = newRootId !== state.scopeRootId;
      state.scopeRootId = newRootId;
      if (changed) {
        // Reset filter on navigation. A filter can be active with an empty query, so this must
        // run on every root change regardless of `status` (unlike the in-flight-search reset below).
        state.assignedToMe = false;
        state.statusFilter = null;
      }
      if (changed && state.status !== "idle") {
        // cancel in-flight: results + requestId reset; selection also cleared
        state.results = [];
        state.page = 0;
        state.hasMore = false;
        state.requestId = null;
        state.selectedDocPartId = null;
        state.selectedScopeRootId = null;
        state.status = "idle";
        state.nextPageStatus = "idle";
        state.error = null;
      }
    },
    resultSelected(state, action: PayloadAction<{ id: string; scopeRootId: string }>) {
      state.selectedDocPartId = action.payload.id;
      state.selectedScopeRootId = action.payload.scopeRootId;
    },
    selectionCleared(state) {
      state.selectedDocPartId = null;
      state.selectedScopeRootId = null;
    },
    searchCleared(state) {
      state.query = "";
      state.effectiveQuery = null;
      state.status = "idle";
      state.results = [];
      state.page = 0;
      state.hasMore = false;
      state.error = null;
      state.nextPageStatus = "idle";
      state.requestId = null;
      // selectedDocPartId and selectedScopeRootId are intentionally preserved for FR-012
      // assignedToMe and statusFilter are intentionally preserved: "X" clears the text query only.
    },
    filterChanged(state, action: PayloadAction<{ assignedToMe: boolean; statusFilter: OutlineFilterStatus | null }>) {
      // Sets the filter fields only; does NOT clear results/status — useOutlineSearch (FE-4) drives fetching.
      state.assignedToMe = action.payload.assignedToMe;
      state.statusFilter = action.payload.statusFilter;
    },
  },
});

export const {
  queryChanged,
  searchStarted,
  searchSucceeded,
  searchFailed,
  nextPageStarted,
  nextPageSucceeded,
  nextPageFailed,
  scopeRootResolved,
  resultSelected,
  selectionCleared,
  searchCleared,
  filterChanged,
} = outlineSearchSlice.actions;

// Selectors
export const selectOutlineSearch = (state: RootState) => state.outlineSearch;
export const selectIsOutlineSearchResultsVisible = (state: RootState): boolean => {
  const s = state.outlineSearch;
  return hasActiveSearchCriteria(s.query, s.assignedToMe, s.statusFilter) && s.status !== "idle";
};
export const selectOutlineSearchResults = (state: RootState) => state.outlineSearch.results;
export const selectOutlineSearchSelectedDocPartId = (state: RootState) => state.outlineSearch.selectedDocPartId;
export const selectOutlineFilter = (state: RootState) => ({
  assignedToMe: state.outlineSearch.assignedToMe,
  statusFilter: state.outlineSearch.statusFilter,
});

export default outlineSearchSlice.reducer;
